/**
 * @file 채널 폴더 관리 — 생성·이름/설명 수정·순서·삭제. PM·채널 관리자 전용.
 *
 * <h3>무엇이 즉시 저장되고 무엇이 '저장' 버튼을 기다리나</h3>
 * <p>섞여 있으면 사용자가 "지금 반영된 건가" 를 매번 의심하게 되므로 기준을 하나로 뒀다 —
 * <b>구조를 바꾸는 것은 즉시, 글자를 고치는 것은 저장 버튼.</b>
 * <ul>
 *   <li>즉시 — 폴더 추가, 순서(↑↓), 삭제. 모두 그 자리에서 결과가 보이는 동작이다.</li>
 *   <li>저장 버튼 — 폴더명·설명. 타이핑 중간값이 서버로 새어 나가지 않게 모아서 보낸다.</li>
 * </ul>
 *
 * <h3>[2026-08-13] 폴더 참가자가 사라졌다</h3>
 * <p>열람·수정 권한이 채널(프로젝트) 참여 인력 하나로 통합됐다. 폴더는 이제 <b>분류 서랍</b>이라
 * 참가자 개념이 없고, 이 화면은 폴더의 이름·설명·순서·삭제만 다룬다.
 * 누가 볼 수 있는지는 채널의 '참여자' 탭에서 정한다. (경위는 백엔드 ChannelFolderAccess 주석)
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ActionButton from "../../../component/common/ActionButton";
import FormField from "../../../component/common/FormField";
import ComboBox from "../../../component/common/ComboBox";
import { channelApi } from "../api/channelApi";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { notify } from "../../../util/toast";
import { runApiAction, getApiErrorMessage, serverMsgOr } from "../../../util/apiFeedback";
import { ds } from "../../../styles/designSystem";

const C = ds.colors;

export default function FolderManageDialog({
    open, channelId, folders = [], onClose, onChanged,
}) {
    const [rows, setRows] = useState([]);
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);
    const [saving, setSaving] = useState(false);
    /** 편집 줄을 펼친 폴더 id 집합. <b>기본은 전부 접힘</b>이고 새로 만든 폴더만 열린다. */
    const [expandedIds, setExpandedIds] = useState(new Set());
    const toggleExpanded = (id) => setExpandedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const isEditorOpen = (row) => expandedIds.has(row.id);

    /**
     * 서버 폴더 목록 → 편집 사본 동기화.
     *
     * <p>여기서 펼침 상태({@code expandedIds})를 건드리면 안 된다 — 목록이 갱신될 때마다 열어 둔
     * 줄이 접혀 편집이 끊긴다. 펼침은 목록 데이터와 무관한 화면 상태다.
     *
     * <p>{@code base} 는 서버 원본 — '저장' 이 <b>바뀐 줄만</b> 보내기 위한 비교 기준이다.
     */
    useEffect(() => {
        if (!open) return;
        setRows(folders.map(f => ({
            id: f.id,
            name: f.name,
            description: f.description || "",
            feedCount: f.feedCount,
            base: { name: f.name, description: f.description || "" },
        })));
    }, [open, folders]);

    /** 다이얼로그를 <b>열 때만</b> 초기화 — 입력 중이던 값·펼침 상태가 갱신 때마다 날아가지 않게. */
    useEffect(() => {
        if (!open) return;
        setNewName("");
        setExpandedIds(new Set());
    }, [open]);

    /** 저장할 것이 있는지 — 없으면 '저장' 을 비활성화해 헛클릭을 막는다. */
    const dirtyRows = useMemo(
        () => rows.filter(r => r.name.trim() !== r.base.name || (r.description || "") !== r.base.description),
        [rows]);

    const handleCreate = async () => {
        const name = newName.trim();
        if (!name) { notify("폴더명을 입력하세요", "error"); return; }
        setCreating(true);
        try {
            const created = await channelApi.createFolder(channelId, { name });
            setNewName("");
            onChanged();
            // 만든 직후 편집 줄을 펼친다 — 대부분 이어서 설명을 적는다.
            if (created?.id) setExpandedIds(prev => new Set(prev).add(created.id));
        } catch (err) {
            notify(getApiErrorMessage(err, "폴더 생성 실패"), "error");
        } finally {
            setCreating(false);
        }
    };

    /** 바뀐 줄만 순차 저장. 하나가 실패해도 나머지는 반영하고, 실패한 폴더명을 알려준다. */
    const handleSaveAll = async () => {
        if (dirtyRows.length === 0) { onClose(); return; }
        const blank = dirtyRows.find(r => !r.name.trim());
        if (blank) { notify("폴더명은 비워 둘 수 없습니다", "error"); return; }

        setSaving(true);
        const failed = [];
        for (const r of dirtyRows) {
            try {
                await channelApi.updateFolder(r.id, {
                    name: r.name.trim(),
                    description: r.description || null,
                });
            } catch (err) {
                failed.push(`${r.name}(${getApiErrorMessage(err, "실패")})`);
            }
        }
        setSaving(false);
        onChanged();

        if (failed.length) notify(`저장 실패: ${failed.join(", ")}`, "error");
        else { notify("폴더를 저장했습니다", "success"); onClose(); }
    };

    /** 위/아래 이동 — 화면 순서를 바꾼 뒤 그 순서를 그대로 서버에 저장한다(즉시). */
    const move = (index, delta) => {
        const next = [...rows];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        setRows(next);
        return runApiAction({
            request: () => channelApi.reorderFolders(channelId, next.map(r => r.id)),
            errorMsg: serverMsgOr("순서 저장 실패"),
            silent: true,
            onSuccess: onChanged,
        });
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>폴더 관리</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Typography sx={{ fontSize: 12.5, color: C.textMuted, mb: 2 }}>
                    한 행사·프로젝트를 부서나 섹션 단위로 나눕니다. 폴더는 <b>분류</b>일 뿐이고,
                    <Box component="span" sx={{ fontWeight: 700, color: C.textSecondary }}> 채널 참여자는 모든 폴더를 </Box>
                    보고 쓸 수 있습니다. 누가 볼지는 '참여자' 탭에서 정합니다.
                </Typography>

                <FormField label="새 폴더" required>
                    <Stack direction="row" spacing={1}>
                        <TextField
                            size="small" fullWidth
                            placeholder="예: 디자인팀, 무대 운영"
                            value={newName}
                            onChange={e => setNewName(e.target.value.slice(0, 100))}
                            /* isComposing — 한글 조합 중 Enter 로 폴더가 두 개 만들어지는 것 방지
                               (ChannelPage 의 폴더 입력과 같은 사유) */
                            onKeyDown={e => {
                                if (e.nativeEvent?.isComposing) return;
                                if (e.key === "Enter") handleCreate();
                            }}
                            sx={{ "& .MuiOutlinedInput-root": { fontSize: 13 } }}
                        />
                        <ActionButton variant="add"
                            startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                            onClick={handleCreate} loading={creating}
                            sx={{ fontSize: 12.5, flexShrink: 0 }} label="추가" />
                    </Stack>
                </FormField>

                {rows.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: "center" }}>
                        <Typography sx={{ fontSize: 13, color: C.textMuted }}>
                            아직 폴더가 없습니다. 위에서 첫 폴더를 만들어 보세요.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.25} mt={1}>
                        {rows.map((row, i) => (
                            <Box key={row.id} sx={{
                                // 회색 채움 대신 <b>테두리</b>로 영역을 잡는다 — 안쪽 입력칸이 흰색이라
                                // 배경까지 회색이면 두 톤이 겹쳐 탁해 보인다(사내 톤 = 그림자 대신 테두리).
                                border: `1px solid ${C.border}`, borderRadius: 1.5,
                                bgcolor: C.surface, px: 1.5, py: 1.25,
                            }}>
                                {/*
                                  * 카드 헤더 — 접힌 상태에서 보이는 유일한 줄.
                                  * 폴더명 + 글 수만 남긴다. 예전엔 참가자 인원이 이 자리였는데, 폴더 권한이
                                  * 사라지면서 «손볼 폴더인지» 를 알려주는 값이 글 수로 바뀌었다
                                  * (비어 있는 폴더인지 = 지워도 되는지).
                                  */}
                                <Stack direction="row" alignItems="center" spacing={0.75}
                                    onClick={() => toggleExpanded(row.id)}
                                    sx={{ cursor: "pointer", "&:hover .folder-card-name": { color: C.primary } }}>
                                    <FolderRoundedIcon sx={{ fontSize: 17, color: "#f59e0b", flexShrink: 0 }} />
                                    <Typography noWrap className="folder-card-name" sx={{
                                        fontSize: 13.5, fontWeight: 700, color: C.textPrimary,
                                        transition: "color .12s", minWidth: 0,
                                    }}>
                                        {row.base.name || "새 폴더"}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, flexShrink: 0, color: C.textMuted }}>
                                        글 {row.feedCount ?? 0}건
                                    </Typography>
                                    <Box flex={1} />
                                    <ExpandLessRoundedIcon sx={{
                                        fontSize: 17, color: C.textMuted, flexShrink: 0,
                                        transition: "transform 160ms ease",
                                        transform: isEditorOpen(row) ? "none" : "rotate(180deg)",
                                    }} />
                                    {/* 순서·삭제는 접혀 있어도 쓸 수 있어야 한다(정리 작업의 대부분이 이 둘이다).
                                        카드 클릭이 접기라서 버블링을 끊는다. */}
                                    <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}
                                        onClick={e => e.stopPropagation()}>
                                        <Tooltip title="위로">
                                            <span>
                                                <IconButton size="small" onClick={() => move(i, -1)}
                                                    disabled={i === 0} sx={{ p: 0.4 }}>
                                                    <ArrowUpwardRoundedIcon sx={{ fontSize: 15, color: C.textMuted }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="아래로">
                                            <span>
                                                <IconButton size="small" onClick={() => move(i, 1)}
                                                    disabled={i === rows.length - 1} sx={{ p: 0.4 }}>
                                                    <ArrowDownwardRoundedIcon sx={{ fontSize: 15, color: C.textMuted }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <DeleteFolderButton
                                            folder={row}
                                            otherFolders={rows.filter(r => r.id !== row.id)}
                                            onDeleted={onChanged}
                                        />
                                    </Stack>
                                </Stack>

                                {/* 펼친 내용 — 이름·설명 입력칸. 안쪽에 또 다른 토글은 두지 않는다. */}
                                <Collapse in={isEditorOpen(row)} unmountOnExit>
                                    <Box sx={{ pt: 1.5 }}>
                                        <Stack direction="row" spacing={1} alignItems="flex-end">
                                            <FormField label="폴더명" required sx={{ flex: 1, minWidth: 0 }}>
                                                <TextField
                                                    size="small" fullWidth
                                                    value={row.name}
                                                    onChange={e => setRows(p => p.map((r, j) =>
                                                        j === i ? { ...r, name: e.target.value.slice(0, 100) } : r))}
                                                    sx={{ "& .MuiOutlinedInput-root": { fontSize: 13 } }}
                                                />
                                            </FormField>
                                            <FormField label="설명" sx={{ flex: 1.4, minWidth: 0 }}>
                                                <TextField
                                                    size="small" fullWidth
                                                    value={row.description}
                                                    onChange={e => setRows(p => p.map((r, j) =>
                                                        j === i ? { ...r, description: e.target.value.slice(0, 300) } : r))}
                                                    sx={{ "& .MuiOutlinedInput-root": { fontSize: 12.5 } }}
                                                />
                                            </FormField>
                                        </Stack>

                                    </Box>
                                </Collapse>
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {/* 닫기는 회색(중립) — 파란 채움은 '저장' 하나만 갖는다.
                    둘 다 파랗면 어느 쪽이 반영되는 버튼인지 눈으로 구분할 수 없다. */}
                <ActionButton variant="cancel" onClick={onClose} disabled={saving} label="닫기" />
                <ActionButton variant="save" onClick={handleSaveAll} loading={saving}
                    disabled={dirtyRows.length === 0}
                    label={dirtyRows.length > 0 ? `저장 (${dirtyRows.length})` : "저장"} />
            </DialogActions>
        </Dialog>
    );
}

/**
 * 폴더 삭제 — <b>내용물을 어디로 옮길지 먼저 고른다.</b>
 *
 * <p>내용물이 어디로 갈지 모르는 채로 지우면 «글이 사라졌다» 가 되므로, 대상을 고르는 단계를
 * 건너뛸 수 없게 셀렉트를 앞에 둔다. (폴더 권한이 없어진 뒤로 이 동작은 순수한 분류 변경이다)
 */
function DeleteFolderButton({ folder, otherFolders, onDeleted }) {
    const [open, setOpen] = useState(false);
    const [target, setTarget] = useState("");   // "" = 미분류
    const [deleting, setDeleting] = useState(false);

    const hasContent = (folder.feedCount ?? 0) > 0;

    const handleDelete = async () => {
        const targetName = target === ""
            ? "미분류"
            : (otherFolders.find(f => String(f.id) === String(target))?.name ?? "선택한 폴더");
        const msg = hasContent
            ? `'${folder.name}' 폴더를 삭제하시겠습니까?\n안의 항목은 삭제되지 않고 '${targetName}'(으)로 옮겨집니다.`
            : `'${folder.name}' 폴더를 삭제하시겠습니까?`;
        if (!(await confirmDialog({ message: msg }))) return;
        return runApiAction({
            request: () => channelApi.deleteFolder(folder.id, target === "" ? undefined : Number(target)),
            successMsg: `폴더를 삭제하고 항목을 '${targetName}'(으)로 옮겼습니다`,
            errorMsg: serverMsgOr("폴더 삭제 실패"),
            setLoading: setDeleting,
            onSuccess: () => { setOpen(false); onDeleted(); },
        });
    };

    if (!open) {
        return (
            <Tooltip title="삭제">
                <IconButton size="small" onClick={() => setOpen(true)} sx={{ p: 0.4 }}>
                    <DeleteRoundedIcon sx={{ fontSize: 16, color: C.error }} />
                </IconButton>
            </Tooltip>
        );
    }

    return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            {/* 드롭다운은 공통 ComboBox — 팝업·항목 디자인은 muiTheme 의 MuiAutocomplete 가 전담한다.
                예전엔 raw Select 에 fontSize 를 직접 박아 이 다이얼로그만 다른 드롭다운으로 보였다. */}
            <ComboBox
                options={[
                    { value: "", label: "미분류로 이동" },
                    ...otherFolders.map(f => ({ value: f.id, label: `${f.name}(으)로` })),
                ]}
                value={target}
                onChange={setTarget}
                clearable={false}
                placeholder="미분류로 이동"
                fullWidth={false}
                sx={{ minWidth: 150, bgcolor: C.surface }}
            />
            <ActionButton variant="delete" onClick={handleDelete} loading={deleting}
                startIcon={false} sx={{ fontSize: 11.5, py: 0.2, minWidth: 0 }} label="삭제" />
            <ActionButton variant="cancel" onClick={() => setOpen(false)}
                sx={{ fontSize: 11.5, py: 0.2, minWidth: 0 }} />
        </Stack>
    );
}
